import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projectApi, columnApi, taskApi, userApi, activityApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Plus, 
  MoreVertical,
  Trash2, 
  ArrowLeft,
  Loader2,
  Calendar,
  Users,
  MessageSquare,
  CheckSquare,
  Send,
  Clock,
  GripVertical
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const KanbanBoard = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  
  const [project, setProject] = useState(null);
  const [columns, setColumns] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Task dialog
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    column_id: '',
    priority: 'medium',
    due_date: '',
    assigned_users: [],
    checklist: []
  });
  
  // Column dialog
  const [columnDialogOpen, setColumnDialogOpen] = useState(false);
  const [newColumn, setNewColumn] = useState({ name: '', color: '#3B82F6' });
  
  // Comment input
  const [newComment, setNewComment] = useState('');
  
  // Activity logs for selected task
  const [taskActivities, setTaskActivities] = useState([]);
  
  // Drag state
  const [draggedTask, setDraggedTask] = useState(null);

  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    try {
      const [projectRes, columnsRes, tasksRes, usersRes] = await Promise.all([
        projectApi.get(projectId),
        columnApi.list(projectId),
        taskApi.list(projectId),
        userApi.list().catch(() => ({ data: [] })),
      ]);
      setProject(projectRes.data);
      setColumns(columnsRes.data);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error loading kanban:', error);
      toast.error('Erro ao carregar projeto');
    } finally {
      setLoading(false);
    }
  };

  const loadTaskActivities = async (taskId) => {
    try {
      const res = await activityApi.list(taskId, 20);
      setTaskActivities(res.data);
    } catch (error) {
      console.error('Error loading activities:', error);
    }
  };

  const handleOpenTask = (task) => {
    setSelectedTask(task);
    setTaskForm({
      title: task.title,
      description: task.description || '',
      column_id: task.column_id,
      priority: task.priority,
      due_date: task.due_date || '',
      assigned_users: task.assigned_users || [],
      checklist: task.checklist || []
    });
    loadTaskActivities(task.id);
    setTaskDialogOpen(true);
  };

  const handleCreateTask = () => {
    if (columns.length === 0) {
      toast.error('Crie uma coluna primeiro');
      return;
    }
    setSelectedTask(null);
    setTaskForm({
      title: '',
      description: '',
      column_id: columns[0].id,
      priority: 'medium',
      due_date: '',
      assigned_users: [],
      checklist: []
    });
    setTaskActivities([]);
    setTaskDialogOpen(true);
  };

  const handleSaveTask = async () => {
    if (!taskForm.title.trim()) {
      toast.error('Digite o título da tarefa');
      return;
    }

    try {
      if (selectedTask) {
        await taskApi.update(selectedTask.id, taskForm);
        toast.success('Tarefa atualizada');
      } else {
        await taskApi.create({
          ...taskForm,
          project_id: projectId
        });
        toast.success('Tarefa criada');
      }
      setTaskDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar tarefa');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Excluir esta tarefa?')) return;
    
    try {
      await taskApi.delete(taskId);
      toast.success('Tarefa excluída');
      setTaskDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir tarefa');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTask) return;
    
    try {
      await taskApi.addComment(selectedTask.id, newComment);
      setNewComment('');
      // Reload task data
      const taskRes = await taskApi.get(selectedTask.id);
      setSelectedTask(taskRes.data);
      loadTaskActivities(selectedTask.id);
      toast.success('Comentário adicionado');
    } catch (error) {
      toast.error('Erro ao adicionar comentário');
    }
  };

  const handleAddChecklistItem = () => {
    const newItem = {
      id: `temp-${Date.now()}`,
      text: '',
      completed: false
    };
    setTaskForm({
      ...taskForm,
      checklist: [...taskForm.checklist, newItem]
    });
  };

  const handleUpdateChecklistItem = (index, updates) => {
    const newChecklist = [...taskForm.checklist];
    newChecklist[index] = { ...newChecklist[index], ...updates };
    setTaskForm({ ...taskForm, checklist: newChecklist });
  };

  const handleRemoveChecklistItem = (index) => {
    const newChecklist = taskForm.checklist.filter((_, i) => i !== index);
    setTaskForm({ ...taskForm, checklist: newChecklist });
  };

  const handleCreateColumn = async () => {
    if (!newColumn.name.trim()) {
      toast.error('Digite o nome da coluna');
      return;
    }

    try {
      await columnApi.create(projectId, {
        name: newColumn.name,
        color: newColumn.color,
        order: columns.length
      });
      toast.success('Coluna criada');
      setNewColumn({ name: '', color: '#3B82F6' });
      setColumnDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao criar coluna');
    }
  };

  const handleDeleteColumn = async (columnId) => {
    if (!window.confirm('Excluir esta coluna?')) return;
    
    try {
      await columnApi.delete(columnId);
      toast.success('Coluna excluída');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir coluna');
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, columnId) => {
    e.preventDefault();
    if (!draggedTask || draggedTask.column_id === columnId) {
      setDraggedTask(null);
      return;
    }

    try {
      await taskApi.move(draggedTask.id, columnId, 0);
      loadData();
    } catch (error) {
      toast.error('Erro ao mover tarefa');
    }
    setDraggedTask(null);
  };

  const getTasksByColumn = (columnId) => {
    return tasks.filter(t => t.column_id === columnId).sort((a, b) => a.order - b.order);
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'priority-low',
      medium: 'priority-medium',
      high: 'priority-high'
    };
    const labels = {
      low: 'Baixa',
      medium: 'Média',
      high: 'Alta'
    };
    return (
      <span className={cn('priority-badge', styles[priority])}>
        {labels[priority]}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="kanban-board" className="h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/projects')}
            data-testid="back-to-projects"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">{project?.name}</h1>
            <p className="text-sm text-slate-500">{project?.description || 'Kanban Board'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            data-testid="add-column-button"
            onClick={() => setColumnDialogOpen(true)}
          >
            <Plus className="w-4 h-4 mr-1" />
            Coluna
          </Button>
          <Button
            size="sm"
            data-testid="add-task-button"
            onClick={handleCreateTask}
            className="btn-primary"
          >
            <Plus className="w-4 h-4 mr-1" />
            Tarefa
          </Button>
        </div>
      </div>

      {/* Kanban Columns */}
      <div className="kanban-scroll pb-4">
        <div className="flex gap-6 min-w-max">
          {columns.map((column) => (
            <div
              key={column.id}
              data-testid={`column-${column.id}`}
              className="kanban-column w-80 flex-shrink-0"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, column.id)}
            >
              {/* Column Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: column.color }}
                  />
                  <h3 className="font-medium text-slate-900">{column.name}</h3>
                  <span className="text-xs text-slate-400 bg-slate-200 px-2 py-0.5 rounded-full">
                    {getTasksByColumn(column.id).length}
                  </span>
                </div>
                {isAdmin() && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => handleDeleteColumn(column.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              {/* Tasks */}
              <div className="space-y-3">
                {getTasksByColumn(column.id).map((task) => (
                  <div
                    key={task.id}
                    data-testid={`task-${task.id}`}
                    className={cn(
                      'task-card',
                      draggedTask?.id === task.id && 'opacity-50'
                    )}
                    draggable
                    onDragStart={(e) => handleDragStart(e, task)}
                    onClick={() => handleOpenTask(task)}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4 className="font-medium text-slate-900 text-sm">{task.title}</h4>
                      <GripVertical className="w-4 h-4 text-slate-300 flex-shrink-0 cursor-grab" />
                    </div>
                    
                    {task.description && (
                      <p className="text-xs text-slate-500 mb-3 line-clamp-2">
                        {task.description}
                      </p>
                    )}

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getPriorityBadge(task.priority)}
                      </div>
                      <div className="flex items-center gap-2 text-slate-400">
                        {task.due_date && (
                          <div className="flex items-center gap-1 text-xs">
                            <Calendar className="w-3 h-3" />
                            {new Date(task.due_date).toLocaleDateString('pt-BR')}
                          </div>
                        )}
                        {task.checklist?.length > 0 && (
                          <div className="flex items-center gap-1 text-xs">
                            <CheckSquare className="w-3 h-3" />
                            {task.checklist.filter(c => c.completed).length}/{task.checklist.length}
                          </div>
                        )}
                        {task.comments?.length > 0 && (
                          <div className="flex items-center gap-1 text-xs">
                            <MessageSquare className="w-3 h-3" />
                            {task.comments.length}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Add column placeholder */}
          {columns.length === 0 && (
            <div className="w-80 flex-shrink-0 border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
              <p className="text-slate-500 mb-4">Nenhuma coluna criada</p>
              <Button
                variant="outline"
                onClick={() => setColumnDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Criar Coluna
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Task Dialog */}
      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedTask ? 'Editar Tarefa' : 'Nova Tarefa'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-3 gap-6">
            {/* Main content - 2 cols */}
            <div className="col-span-2 space-y-4">
              <div className="space-y-2">
                <Label>Título</Label>
                <Input
                  data-testid="task-title-input"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder="Título da tarefa"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea
                  data-testid="task-description-input"
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  placeholder="Descrição detalhada..."
                  rows={4}
                />
              </div>

              {/* Checklist */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <CheckSquare className="w-4 h-4" />
                    Checklist
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleAddChecklistItem}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar
                  </Button>
                </div>
                <div className="space-y-2">
                  {taskForm.checklist.map((item, index) => (
                    <div key={item.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={item.completed}
                        onCheckedChange={(checked) => 
                          handleUpdateChecklistItem(index, { completed: checked })
                        }
                      />
                      <Input
                        value={item.text}
                        onChange={(e) => 
                          handleUpdateChecklistItem(index, { text: e.target.value })
                        }
                        placeholder="Item do checklist"
                        className="flex-1 h-8"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400 hover:text-red-500"
                        onClick={() => handleRemoveChecklistItem(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Comments (only for existing tasks) */}
              {selectedTask && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Comentários
                  </Label>
                  <div className="border rounded-lg p-3 bg-slate-50">
                    <ScrollArea className="h-32 mb-3">
                      {selectedTask.comments?.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4">
                          Nenhum comentário
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {selectedTask.comments?.map((comment) => (
                            <div key={comment.id} className="text-sm">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-900">
                                  {comment.user_name}
                                </span>
                                <span className="text-xs text-slate-400">
                                  {formatDistanceToNow(new Date(comment.created_at), {
                                    addSuffix: true,
                                    locale: ptBR
                                  })}
                                </span>
                              </div>
                              <p className="text-slate-600 mt-1">{comment.text}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                    <div className="flex gap-2">
                      <Input
                        data-testid="comment-input"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Adicionar comentário..."
                        className="flex-1"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                      />
                      <Button
                        size="icon"
                        onClick={handleAddComment}
                        disabled={!newComment.trim()}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar - 1 col */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Coluna</Label>
                <Select
                  value={taskForm.column_id}
                  onValueChange={(value) => setTaskForm({ ...taskForm, column_id: value })}
                >
                  <SelectTrigger data-testid="task-column-select">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {columns.map((col) => (
                      <SelectItem key={col.id} value={col.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: col.color }}
                          />
                          {col.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Prioridade</Label>
                <Select
                  value={taskForm.priority}
                  onValueChange={(value) => setTaskForm({ ...taskForm, priority: value })}
                >
                  <SelectTrigger data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Baixa</SelectItem>
                    <SelectItem value="medium">Média</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Data de Entrega
                </Label>
                <Input
                  type="date"
                  data-testid="task-due-date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                />
              </div>

              {/* Activity Log */}
              {selectedTask && taskActivities.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Histórico
                  </Label>
                  <ScrollArea className="h-32 border rounded-lg p-2 bg-slate-50">
                    <div className="space-y-2 text-xs">
                      {taskActivities.map((activity) => (
                        <div key={activity.id} className="pb-2 border-b border-slate-100 last:border-0">
                          <p className="text-slate-600">
                            <span className="font-medium">{activity.user_name}</span>
                            {' '}{activity.action}{' '}
                            <span className="text-slate-500">{activity.details}</span>
                          </p>
                          <p className="text-slate-400 mt-0.5">
                            {formatDistanceToNow(new Date(activity.created_at), {
                              addSuffix: true,
                              locale: ptBR
                            })}
                          </p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex justify-between">
            <div>
              {selectedTask && (
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDeleteTask(selectedTask.id)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Excluir
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setTaskDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveTask} data-testid="save-task-button">
                Salvar
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column Dialog */}
      <Dialog open={columnDialogOpen} onOpenChange={setColumnDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Coluna</DialogTitle>
            <DialogDescription>
              Adicione uma nova coluna ao quadro Kanban
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome da Coluna</Label>
              <Input
                data-testid="column-name-input"
                value={newColumn.name}
                onChange={(e) => setNewColumn({ ...newColumn, name: e.target.value })}
                placeholder="Ex: Em Revisão"
              />
            </div>
            <div className="space-y-2">
              <Label>Cor</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={newColumn.color}
                  onChange={(e) => setNewColumn({ ...newColumn, color: e.target.value })}
                  className="w-10 h-10 rounded cursor-pointer"
                />
                <Input
                  value={newColumn.color}
                  onChange={(e) => setNewColumn({ ...newColumn, color: e.target.value })}
                  className="w-28"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setColumnDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateColumn} data-testid="create-column-submit">
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KanbanBoard;
